import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Search,
    PlayCircle,
    CheckCircle,
    XCircle,
    Code2,
    ChevronDown,
    ChevronUp,
    Component,
    FileText,
    Trash2,
    Edit,
    List,
    Rocket,
    Bot,
    Globe,
} from "lucide-react";
import BrowserAgentPreview from "./BrowserAgentPreview";

const ExpandableText = ({ text, maxLength = 150 }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!text || text.length <= maxLength) {
        return <span className="break-words">{text}</span>;
    }

    return (
        <div className="break-words overflow-hidden">
            <span className="break-words">
                {isExpanded ? text : `${text.substring(0, maxLength)}...`}
            </span>
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="ml-2 text-blue-400 hover:text-blue-300 inline-flex items-center gap-0.5 flex-shrink-0"
            >
                {isExpanded ? (
                    <>
                        <span>Show less</span>
                        <ChevronUp className="h-3 w-3" />
                    </>
                ) : (
                    <>
                        <span>Show more</span>
                        <ChevronDown className="h-3 w-3" />
                    </>
                )}
            </button>
        </div>
    );
};

// Helper function to extract reason text from string or object
const getReasonText = (reason) => {
    if (!reason) return null;
    if (typeof reason === "string") return reason;
    if (typeof reason === "object" && reason.description)
        return reason.description;
    return null;
};

// Map tool names to types (matching ToolResultProcessor mapping)
const TOOL_TYPE_MAP = {
    read_files: "file_reading",
    execute_code: "code_execution",
    update_or_create_file: "code_generation",
    create_file: "code_generation",
    update_file: "code_generation",
    scaffold_app: "app_scaffolding",
    add_route: "routing",
    create_ui_component: "component_creation",
    search_in_files: "file_search",
    grep_search: "grep_search",
    line_replace: "line_replace",
    edit_file: "file_editing",
    report_planned_items: "planning",
    start_code_generation: "generation_start",
    delete_file: "file_deletion",
    rename_file: "file_rename",
    read_application_logs: "read_application_logs",
    todo_write: "task_management",
    browser_agent: "browser_agent",
};

// Helper to get normalized type from item (checks type first, then maps toolName)
const getItemType = (item) => {
    if (item.type) return item.type;
    if (item.toolName && TOOL_TYPE_MAP[item.toolName]) {
        return TOOL_TYPE_MAP[item.toolName];
    }
    return item.type || "unknown";
};

// Types that have explicit handlers in the component
const HANDLED_TYPES = [
    "api_search",
    "code_execution",
    "code_generation",
    "file_reading",
    "file_search",
    "component_creation",
    "file_deletion",
    "browser_agent_session",
    "browser_agent_step",
    "browser_agent_complete",
    "browser_agent_error",
    "file_editing",
    "line_replace",
    "grep_search",
    "read_application_logs",
    "planning",
    "todo_write",
    "task_management",
    "generation_start",
];

// Component to show subagent indicator
const SubagentBadge = ({ agentName }) => {
    if (!agentName) return null;
    return (
        <span className="text-[10px] px-1.5 py-0.5 bg-purple-900/30 rounded border border-purple-700/50 flex items-center gap-1">
            <Bot className="h-2.5 w-2.5 text-purple-400" />
            <span className="text-purple-400">via {agentName}</span>
        </span>
    );
};

export const CodeGenerationFeedback = ({ feedback = [] }) => {
    if (!feedback || feedback.length === 0) {
        return null;
    }

    // Filter browser agent feedback items for dedicated preview component
    const browserAgentFeedback = feedback.filter(
        (item) =>
            item.type?.startsWith("browser_agent_") ||
            item.toolName === "browser_agent"
    );
    const otherFeedback = feedback.filter(
        (item) =>
            !item.type?.startsWith("browser_agent_") &&
            item.toolName !== "browser_agent"
    );

    return (
        <div className="mt-3 space-y-2 overflow-hidden">
            {/* Browser Agent Preview - rendered separately since it accumulates multiple feedback items */}
            {browserAgentFeedback.length > 0 && (
                <BrowserAgentPreview feedback={browserAgentFeedback} />
            )}

            <AnimatePresence mode="popLayout">
                {otherFeedback.map((item, index) => (
                    <motion.div
                        key={item.id || `${item.timestamp}-${index}`}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 10 }}
                        transition={{ duration: 0.2 }}
                        className="text-xs p-2 bg-gray-800 rounded border border-gray-700 overflow-hidden"
                    >
                        {getItemType(item) === "api_search" && (
                            <div className="flex items-start gap-2">
                                <Search className="h-3.5 w-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-blue-400 font-medium">
                                            {item.result
                                                ? "Searched API:"
                                                : "Searching API:"}
                                        </span>
                                        <span className="text-gray-300 break-words">
                                            {item.query}
                                        </span>
                                        {item.integration && (
                                            <span className="text-purple-400 text-[10px] px-1.5 py-0.5 bg-purple-900/30 rounded">
                                                {item.integration}
                                            </span>
                                        )}
                                        <SubagentBadge
                                            agentName={item.agentName}
                                        />
                                    </div>
                                    {item.result && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {item.result.success ? (
                                                <>
                                                    <CheckCircle className="h-3 w-3 text-green-400" />
                                                    <span className="text-green-400 text-[10px]">
                                                        Found{" "}
                                                        {
                                                            item.result
                                                                .exampleCount
                                                        }{" "}
                                                        example
                                                        {item.result
                                                            .exampleCount !== 1
                                                            ? "s"
                                                            : ""}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle className="h-3 w-3 text-yellow-400" />
                                                    <span className="text-yellow-400 text-[10px]">
                                                        No examples found
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {getItemType(item) === "code_execution" && (
                            <div className="flex items-start gap-2">
                                <PlayCircle className="h-3.5 w-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-amber-400 font-medium">
                                            {item.result
                                                ? "Ran code:"
                                                : "Running code:"}
                                        </span>
                                        <span className="text-gray-300 break-words text-[10px]">
                                            {item.description}
                                        </span>
                                        <SubagentBadge
                                            agentName={item.agentName}
                                        />
                                    </div>
                                    {item.result && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {item.result.success ? (
                                                <>
                                                    <CheckCircle className="h-3 w-3 text-green-400" />
                                                    <span className="text-green-400 text-[10px]">
                                                        Success
                                                        {item.result
                                                            .hasOutput &&
                                                            " • Data extracted"}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle className="h-3 w-3 text-red-400" />
                                                    <span className="text-red-400 text-[10px]">
                                                        Failed
                                                        {item.result.error &&
                                                            `: ${item.result.error}`}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {getItemType(item) === "code_generation" && (
                            <div className="flex items-start gap-2">
                                <Code2 className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-emerald-400 font-medium">
                                            {item.result
                                                ? "Wrote:"
                                                : "Writing..."}
                                        </span>
                                        {item.changeType && (
                                            <span className="text-cyan-400 text-[10px] px-1.5 py-0.5 bg-cyan-900/30 rounded">
                                                {item.changeType.replace(
                                                    /_/g,
                                                    " "
                                                )}
                                            </span>
                                        )}
                                        <SubagentBadge
                                            agentName={item.agentName}
                                        />
                                    </div>
                                    {/* Show description or text (prefer result description if available) */}
                                    {(item.result?.description ||
                                        item.description ||
                                        item.text) &&
                                        (
                                            item.result?.description ||
                                            item.description ||
                                            item.text
                                        ).trim() && (
                                            <div className="mt-1 text-gray-300 text-[10px]">
                                                <ExpandableText
                                                    text={
                                                        item.result
                                                            ?.description ||
                                                        item.description ||
                                                        item.text
                                                    }
                                                    maxLength={150}
                                                />
                                            </div>
                                        )}
                                    {item.result && (
                                        <div className="mt-1">
                                            <div className="flex items-center gap-1.5">
                                                {item.result.success ? (
                                                    <>
                                                        <CheckCircle className="h-3 w-3 text-green-400" />
                                                        <span className="text-green-400 text-[10px]">
                                                            {
                                                                item.result
                                                                    .codeLength
                                                            }{" "}
                                                            characters
                                                            {item.result
                                                                .isComplete &&
                                                                " • Complete"}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <XCircle className="h-3 w-3 text-red-400" />
                                                        <span className="text-red-400 text-[10px]">
                                                            Failed
                                                            {item.result
                                                                .error &&
                                                                `: ${item.result.error}`}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {getItemType(item) === "file_reading" && (
                            <div className="flex items-start gap-2">
                                <FileText className="h-3.5 w-3.5 text-indigo-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-indigo-400 font-medium">
                                            {item.result
                                                ? "Looked at:"
                                                : "Looking at..."}
                                        </span>
                                        {getReasonText(item.args?.reason) && (
                                            <span className="text-gray-300 text-[10px]">
                                                {getReasonText(
                                                    item.args.reason
                                                )}
                                            </span>
                                        )}
                                        <SubagentBadge
                                            agentName={item.agentName}
                                        />
                                    </div>
                                    {Array.isArray(item.args?.files) &&
                                        item.args.files.length > 0 && (
                                            <div className="mt-1 text-gray-400 text-[10px]">
                                                {item.args.files
                                                    .slice(0, 3)
                                                    .map(f => typeof f === 'string' ? f : (f?.name || f?.fileName || 'file'))
                                                    .join(", ")}
                                                {item.args.files.length > 3 &&
                                                    ` +${
                                                        item.args.files.length -
                                                        3
                                                    } more`}
                                            </div>
                                        )}
                                    {item.result && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {item.result.success ? (
                                                <>
                                                    <CheckCircle className="h-3 w-3 text-green-400" />
                                                    <span className="text-green-400 text-[10px]">
                                                        Read{" "}
                                                        {item.result
                                                            .filesRead ||
                                                            0}{" "}
                                                        of{" "}
                                                        {item.result
                                                            .totalRequested ||
                                                            0}{" "}
                                                        file
                                                        {item.result
                                                            .totalRequested !==
                                                        1
                                                            ? "s"
                                                            : ""}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle className="h-3 w-3 text-red-400" />
                                                    <span className="text-red-400 text-[10px]">
                                                        Failed
                                                        {item.result.error &&
                                                            `: ${item.result.error}`}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {getItemType(item) === "file_search" && (
                            <div className="flex items-start gap-2">
                                <Search className="h-3.5 w-3.5 text-orange-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-orange-400 font-medium">
                                            {item.result
                                                ? "Found in:"
                                                : "Finding..."}
                                        </span>
                                        <span className="text-gray-300 break-words text-[10px]">
                                            "{item.args?.searchTerm}"
                                        </span>
                                        {item.args?.caseSensitive && (
                                            <span className="text-orange-400 text-[10px] px-1.5 py-0.5 bg-orange-900/30 rounded">
                                                Case sensitive
                                            </span>
                                        )}
                                        <SubagentBadge
                                            agentName={item.agentName}
                                        />
                                    </div>
                                    {getReasonText(item.args?.reason) && (
                                        <div className="mt-1 text-gray-300 text-[10px]">
                                            {getReasonText(item.args.reason)}
                                        </div>
                                    )}
                                    {item.args?.files &&
                                        item.args.files.length > 0 && (
                                            <div className="mt-1 text-gray-400 text-[10px]">
                                                In:{" "}
                                                {item.args.files
                                                    .slice(0, 3)
                                                    .join(", ")}
                                                {item.args.files.length > 3 &&
                                                    ` +${
                                                        item.args.files.length -
                                                        3
                                                    } more`}
                                            </div>
                                        )}
                                    {item.result && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {item.result.success ? (
                                                <>
                                                    <CheckCircle className="h-3 w-3 text-green-400" />
                                                    <span className="text-green-400 text-[10px]">
                                                        {item.result.message}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle className="h-3 w-3 text-red-400" />
                                                    <span className="text-red-400 text-[10px]">
                                                        Failed
                                                        {item.result.error &&
                                                            `: ${item.result.error}`}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {/* Show search results if available */}
                                    {item.result?.success &&
                                        item.result.matches &&
                                        item.result.matches.length > 0 && (
                                            <div className="mt-2 space-y-1">
                                                {item.result.matches
                                                    .slice(0, 3)
                                                    .map((fileMatch, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="bg-gray-700/50 rounded p-2 text-[10px]"
                                                        >
                                                            <div className="text-blue-300 font-medium mb-1">
                                                                {
                                                                    fileMatch.fileName
                                                                }{" "}
                                                                (
                                                                {
                                                                    fileMatch.totalMatches
                                                                }{" "}
                                                                match
                                                                {fileMatch.totalMatches !==
                                                                1
                                                                    ? "es"
                                                                    : ""}
                                                                )
                                                            </div>
                                                            {(
                                                                fileMatch.matches ||
                                                                []
                                                            )
                                                                .slice(0, 2)
                                                                .map(
                                                                    (
                                                                        match,
                                                                        matchIdx
                                                                    ) => (
                                                                        <div
                                                                            key={
                                                                                matchIdx
                                                                            }
                                                                            className="text-gray-300 mb-1"
                                                                        >
                                                                            <span className="text-yellow-400">
                                                                                Line{" "}
                                                                                {
                                                                                    match.lineNumber
                                                                                }

                                                                                :
                                                                            </span>
                                                                            <div className="mt-0.5 bg-gray-800/50 rounded p-1 font-mono text-[9px] whitespace-pre-wrap">
                                                                                {
                                                                                    match.context
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                )}
                                                            {(fileMatch.matches
                                                                ?.length || 0) >
                                                                2 && (
                                                                <div className="text-gray-400 text-[9px]">
                                                                    +
                                                                    {fileMatch
                                                                        .matches
                                                                        .length -
                                                                        2}{" "}
                                                                    more matches
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                {item.result.matches.length >
                                                    3 && (
                                                    <div className="text-gray-400 text-[9px]">
                                                        +
                                                        {item.result.matches
                                                            .length - 3}{" "}
                                                        more files with matches
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                </div>
                            </div>
                        )}
                        {getItemType(item) === "component_creation" && (
                            <div className="flex items-start gap-2">
                                <Component className="h-3.5 w-3.5 text-purple-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-purple-400 font-medium">
                                            {item.result
                                                ? "Built:"
                                                : "Building..."}
                                        </span>
                                        <span className="text-gray-300 break-words">
                                            {item.componentName}
                                        </span>
                                        {item.componentType && (
                                            <span className="text-purple-400 text-[10px] px-1.5 py-0.5 bg-purple-900/30 rounded">
                                                {item.componentType}
                                            </span>
                                        )}
                                        <SubagentBadge
                                            agentName={item.agentName}
                                        />
                                    </div>
                                    {item.description && (
                                        <div className="mt-1 text-gray-300 text-[10px]">
                                            <ExpandableText
                                                text={item.description}
                                                maxLength={150}
                                            />
                                        </div>
                                    )}
                                    {item.result && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {item.result.success ? (
                                                <>
                                                    <CheckCircle className="h-3 w-3 text-green-400" />
                                                    <span className="text-green-400 text-[10px]">
                                                        {item.componentName}{" "}
                                                        created
                                                        {item.propsCount > 0 &&
                                                            ` • ${
                                                                item.propsCount
                                                            } prop${
                                                                item.propsCount !==
                                                                1
                                                                    ? "s"
                                                                    : ""
                                                            }`}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle className="h-3 w-3 text-red-400" />
                                                    <span className="text-red-400 text-[10px]">
                                                        Failed
                                                        {item.result.error &&
                                                            `: ${item.result.error}`}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {getItemType(item) === "file_deletion" && (
                            <div className="flex items-start gap-2">
                                <Trash2 className="h-3.5 w-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-red-400 font-medium">
                                            {item.result
                                                ? "Removed:"
                                                : "Removing..."}
                                        </span>
                                        <span className="text-gray-300 break-words text-[10px]">
                                            {item.args?.filePath ||
                                                item.result?.filePath}
                                        </span>
                                        <SubagentBadge
                                            agentName={item.agentName}
                                        />
                                    </div>
                                    {(getReasonText(item.args?.reason) ||
                                        getReasonText(item.result?.reason)) && (
                                        <div className="mt-1 text-gray-300 text-[10px]">
                                            <ExpandableText
                                                text={
                                                    getReasonText(
                                                        item.args?.reason
                                                    ) ||
                                                    getReasonText(
                                                        item.result?.reason
                                                    )
                                                }
                                                maxLength={150}
                                            />
                                        </div>
                                    )}
                                    {item.result && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {item.result.success ? (
                                                <>
                                                    <CheckCircle className="h-3 w-3 text-green-400" />
                                                    <span className="text-green-400 text-[10px]">
                                                        {item.result.message ||
                                                            "File deleted"}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle className="h-3 w-3 text-red-400" />
                                                    <span className="text-red-400 text-[10px]">
                                                        Failed
                                                        {item.result.error &&
                                                            `: ${item.result.error}`}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {getItemType(item) === "file_editing" && (
                            <div className="flex items-start gap-2">
                                <Edit className="h-3.5 w-3.5 text-teal-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-teal-400 font-medium">
                                            {item.result
                                                ? "Edited:"
                                                : "Editing..."}
                                        </span>
                                        <span className="text-gray-300 break-words text-[10px]">
                                            {item.args?.file_path ||
                                                item.result?.file_path}
                                        </span>
                                        <SubagentBadge
                                            agentName={item.agentName}
                                        />
                                    </div>
                                    {item.args?.aiFileDescription && (
                                        <div className="mt-1 text-gray-300 text-[10px]">
                                            <ExpandableText
                                                text={item.args.aiFileDescription}
                                                maxLength={150}
                                            />
                                        </div>
                                    )}
                                    {item.result && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {item.result.success ? (
                                                <>
                                                    <CheckCircle className="h-3 w-3 text-green-400" />
                                                    <span className="text-green-400 text-[10px]">
                                                        {item.result.message ||
                                                            "File edited"}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle className="h-3 w-3 text-red-400" />
                                                    <span className="text-red-400 text-[10px]">
                                                        Failed
                                                        {item.result.error &&
                                                            `: ${item.result.error}`}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {getItemType(item) === "line_replace" && (
                            <div className="flex items-start gap-2">
                                <Edit className="h-3.5 w-3.5 text-teal-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-teal-400 font-medium">
                                            {item.result
                                                ? "Updated:"
                                                : "Updating..."}
                                        </span>
                                        <span className="text-gray-300 break-words text-[10px]">
                                            {item.args?.file_path ||
                                                item.result?.file_path}
                                        </span>
                                        {item.args?.first_replaced_line &&
                                            item.args?.last_replaced_line && (
                                                <span className="text-teal-400 text-[10px] px-1.5 py-0.5 bg-teal-900/30 rounded">
                                                    Lines{" "}
                                                    {
                                                        item.args
                                                            .first_replaced_line
                                                    }
                                                    -
                                                    {
                                                        item.args
                                                            .last_replaced_line
                                                    }
                                                </span>
                                            )}
                                        <SubagentBadge
                                            agentName={item.agentName}
                                        />
                                    </div>
                                    {item.result && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {item.result.success ? (
                                                <>
                                                    <CheckCircle className="h-3 w-3 text-green-400" />
                                                    <span className="text-green-400 text-[10px]">
                                                        {item.result.message ||
                                                            `Replaced ${
                                                                item.result
                                                                    .lines_removed ||
                                                                0
                                                            } line(s) with ${
                                                                item.result
                                                                    .lines_added ||
                                                                0
                                                            } line(s)`}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle className="h-3 w-3 text-red-400" />
                                                    <span className="text-red-400 text-[10px]">
                                                        Failed
                                                        {item.result.error &&
                                                            `: ${item.result.error}`}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {getItemType(item) === "grep_search" && (
                            <div className="flex items-start gap-2">
                                <Search className="h-3.5 w-3.5 text-pink-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-pink-400 font-medium">
                                            {item.result
                                                ? "Searched in:"
                                                : "Searching in..."}
                                        </span>
                                        <span className="text-gray-300 break-words text-[10px]">
                                            "
                                            {item.args?.pattern ||
                                                item.result?.pattern}
                                            "
                                        </span>
                                        {item.args?.glob && (
                                            <span className="text-pink-400 text-[10px] px-1.5 py-0.5 bg-pink-900/30 rounded">
                                                {item.args.glob}
                                            </span>
                                        )}
                                        {item.args?.case_insensitive && (
                                            <span className="text-pink-400 text-[10px] px-1.5 py-0.5 bg-pink-900/30 rounded">
                                                Case insensitive
                                            </span>
                                        )}
                                        <SubagentBadge
                                            agentName={item.agentName}
                                        />
                                    </div>
                                    {getReasonText(item.args?.reason) && (
                                        <div className="mt-1 text-gray-300 text-[10px]">
                                            {getReasonText(item.args.reason)}
                                        </div>
                                    )}
                                    {item.result && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {item.result.success ? (
                                                <>
                                                    <CheckCircle className="h-3 w-3 text-green-400" />
                                                    <span className="text-green-400 text-[10px]">
                                                        Found{" "}
                                                        {item.result
                                                            .totalMatches ||
                                                            0}{" "}
                                                        match
                                                        {item.result
                                                            .totalMatches !== 1
                                                            ? "es"
                                                            : ""}{" "}
                                                        in{" "}
                                                        {item.result
                                                            .filesWithMatches ||
                                                            0}{" "}
                                                        file
                                                        {item.result
                                                            .filesWithMatches !==
                                                        1
                                                            ? "s"
                                                            : ""}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle className="h-3 w-3 text-red-400" />
                                                    <span className="text-red-400 text-[10px]">
                                                        Failed
                                                        {item.result.error &&
                                                            `: ${item.result.error}`}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {/* Show search results if available */}
                                    {item.result?.success &&
                                        item.result.matches &&
                                        item.result.matches.length > 0 && (
                                            <div className="mt-2 space-y-1">
                                                {item.result.matches
                                                    .slice(0, 3)
                                                    .map((fileMatch, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="bg-gray-700/50 rounded p-2 text-[10px]"
                                                        >
                                                            <div className="text-blue-300 font-medium mb-1">
                                                                {
                                                                    fileMatch.fileName
                                                                }{" "}
                                                                (
                                                                {
                                                                    fileMatch.totalMatches
                                                                }{" "}
                                                                match
                                                                {fileMatch.totalMatches !==
                                                                1
                                                                    ? "es"
                                                                    : ""}
                                                                )
                                                            </div>
                                                            {(
                                                                fileMatch.matches ||
                                                                []
                                                            )
                                                                .slice(0, 2)
                                                                .map(
                                                                    (
                                                                        match,
                                                                        matchIdx
                                                                    ) => (
                                                                        <div
                                                                            key={
                                                                                matchIdx
                                                                            }
                                                                            className="text-gray-300 mb-1"
                                                                        >
                                                                            <div className="mt-0.5 bg-gray-800/50 rounded p-1 font-mono text-[9px] whitespace-pre-wrap">
                                                                                {
                                                                                    match.matchWithContext
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                )}
                                                            {(fileMatch.matches
                                                                ?.length || 0) >
                                                                2 && (
                                                                <div className="text-gray-400 text-[9px]">
                                                                    +
                                                                    {fileMatch
                                                                        .matches
                                                                        .length -
                                                                        2}{" "}
                                                                    more matches
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                {item.result.matches.length >
                                                    3 && (
                                                    <div className="text-gray-400 text-[9px]">
                                                        +
                                                        {item.result.matches
                                                            .length - 3}{" "}
                                                        more files with matches
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                </div>
                            </div>
                        )}
                        {getItemType(item) === "read_application_logs" && (
                            <div className="flex items-start gap-2">
                                <Search className="h-3.5 w-3.5 text-cyan-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-cyan-400 font-medium">
                                            {item.result
                                                ? "Searched logs:"
                                                : "Searching logs..."}
                                        </span>
                                        <span className="text-gray-300 break-words text-[10px]">
                                            "
                                            {item.args?.url_pattern ||
                                                item.result?.url_pattern}
                                            "
                                        </span>
                                        {item.args?.method && (
                                            <span className="text-cyan-400 text-[10px] px-1.5 py-0.5 bg-cyan-900/30 rounded">
                                                {item.args.method}
                                            </span>
                                        )}
                                        <SubagentBadge
                                            agentName={item.agentName}
                                        />
                                    </div>
                                    {getReasonText(item.args?.reason) && (
                                        <div className="mt-1 text-gray-300 text-[10px]">
                                            {getReasonText(item.args.reason)}
                                        </div>
                                    )}
                                    {item.result && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {item.result.success ? (
                                                <>
                                                    <CheckCircle className="h-3 w-3 text-green-400" />
                                                    <span className="text-green-400 text-[10px]">
                                                        Found{" "}
                                                        {item.result.count || 0}{" "}
                                                        match
                                                        {item.result.count !== 1
                                                            ? "es"
                                                            : ""}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle className="h-3 w-3 text-red-400" />
                                                    <span className="text-red-400 text-[10px]">
                                                        Failed
                                                        {item.result.error &&
                                                            `: ${item.result.error}`}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {/* Show matches if available */}
                                    {item.result?.success &&
                                        item.result.matches &&
                                        item.result.matches.length > 0 && (
                                            <div className="mt-2 space-y-1">
                                                {item.result.matches
                                                    .slice(0, 3)
                                                    .map((match, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="bg-gray-700/50 rounded p-2 text-[10px]"
                                                        >
                                                            <div className="text-blue-300 font-medium mb-1 truncate">
                                                                {
                                                                    match
                                                                        .request
                                                                        ?.method
                                                                }{" "}
                                                                {
                                                                    match
                                                                        .request
                                                                        ?.url
                                                                }
                                                            </div>
                                                            <div className="text-gray-400">
                                                                Status:{" "}
                                                                <span
                                                                    className={
                                                                        match
                                                                            .response
                                                                            ?.status >=
                                                                            200 &&
                                                                        match
                                                                            .response
                                                                            ?.status <
                                                                            300
                                                                            ? "text-green-400"
                                                                            : "text-red-400"
                                                                    }
                                                                >
                                                                    {
                                                                        match
                                                                            .response
                                                                            ?.status
                                                                    }
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                {item.result.matches.length >
                                                    3 && (
                                                    <div className="text-gray-400 text-[9px]">
                                                        +
                                                        {item.result.matches
                                                            .length - 3}{" "}
                                                        more matches
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                </div>
                            </div>
                        )}
                        {getItemType(item) === "planning" && (
                            <div className="flex items-start gap-2">
                                <List className="h-3.5 w-3.5 text-violet-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-violet-400 font-medium">
                                            {item.result
                                                ? "Planned items:"
                                                : "Planning..."}
                                        </span>
                                        <SubagentBadge
                                            agentName={item.agentName}
                                        />
                                    </div>
                                    {item.result?.summary && (
                                        <div className="mt-1 text-gray-300 text-[10px]">
                                            <ExpandableText
                                                text={item.result.summary}
                                                maxLength={150}
                                            />
                                        </div>
                                    )}
                                    {item.result?.counts && (
                                        <div className="mt-1 flex flex-wrap gap-2 text-[10px]">
                                            {item.result.counts.entities >
                                                0 && (
                                                <span className="text-violet-300">
                                                    {
                                                        item.result.counts
                                                            .entities
                                                    }{" "}
                                                    entit
                                                    {item.result.counts
                                                        .entities !== 1
                                                        ? "ies"
                                                        : "y"}
                                                </span>
                                            )}
                                            {item.result.counts.pages > 0 && (
                                                <span className="text-violet-300">
                                                    {item.result.counts.pages}{" "}
                                                    page
                                                    {item.result.counts
                                                        .pages !== 1
                                                        ? "s"
                                                        : ""}
                                                </span>
                                            )}
                                            {item.result.counts.components >
                                                0 && (
                                                <span className="text-violet-300">
                                                    {
                                                        item.result.counts
                                                            .components
                                                    }{" "}
                                                    component
                                                    {item.result.counts
                                                        .components !== 1
                                                        ? "s"
                                                        : ""}
                                                </span>
                                            )}
                                            {item.result.counts.hasLayout && (
                                                <span className="text-violet-300">
                                                    Layout
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {item.result && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {item.result.success ? (
                                                <>
                                                    <CheckCircle className="h-3 w-3 text-green-400" />
                                                    <span className="text-green-400 text-[10px]">
                                                        {item.result.message ||
                                                            "Plan recorded"}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle className="h-3 w-3 text-red-400" />
                                                    <span className="text-red-400 text-[10px]">
                                                        Failed
                                                        {item.result.error &&
                                                            `: ${item.result.error}`}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {(getItemType(item) === "todo_write" ||
                            getItemType(item) === "task_management" ||
                            (getItemType(item) === "unknown" &&
                                item.toolName === "todo_write")) && (
                            <div className="flex items-start gap-2">
                                <List className="h-3.5 w-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-blue-400 font-medium">
                                            {item.result
                                                ? "Organized tasks:"
                                                : "Organizing tasks..."}
                                        </span>
                                        <SubagentBadge
                                            agentName={item.agentName}
                                        />
                                    </div>
                                    {item.result?.taskCount > 0 && (
                                        <div className="mt-1 text-gray-300 text-[10px]">
                                            {item.result.taskCount} task
                                            {item.result.taskCount !== 1
                                                ? "s"
                                                : ""}{" "}
                                            updated
                                        </div>
                                    )}
                                    {item.result && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {item.result.success ? (
                                                <>
                                                    <CheckCircle className="h-3 w-3 text-green-400" />
                                                    <span className="text-green-400 text-[10px]">
                                                        {item.result.message ||
                                                            "Tasks updated"}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle className="h-3 w-3 text-red-400" />
                                                    <span className="text-red-400 text-[10px]">
                                                        Failed
                                                        {item.result.error &&
                                                            `: ${item.result.error}`}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {getItemType(item) === "generation_start" && (
                            <div className="flex items-start gap-2">
                                <Rocket className="h-3.5 w-3.5 text-cyan-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-cyan-400 font-medium">
                                            {item.result
                                                ? "Starting generation:"
                                                : "Starting code generation..."}
                                        </span>
                                        {item.args?.generationType && (
                                            <span className="text-cyan-400 text-[10px] px-1.5 py-0.5 bg-cyan-900/30 rounded">
                                                {item.args.generationType.replace(
                                                    /_/g,
                                                    " "
                                                )}
                                            </span>
                                        )}
                                        <SubagentBadge
                                            agentName={item.agentName}
                                        />
                                    </div>
                                    {(item.args?.summary ||
                                        item.result?.summary) && (
                                        <div className="mt-1 text-gray-300 text-[10px]">
                                            <ExpandableText
                                                text={
                                                    item.args?.summary ||
                                                    item.result?.summary
                                                }
                                                maxLength={150}
                                            />
                                        </div>
                                    )}
                                    {item.result && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {item.result.success ? (
                                                <>
                                                    <CheckCircle className="h-3 w-3 text-green-400" />
                                                    <span className="text-green-400 text-[10px]">
                                                        {item.result.message ||
                                                            "Generation started"}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle className="h-3 w-3 text-red-400" />
                                                    <span className="text-red-400 text-[10px]">
                                                        Failed
                                                        {item.result.error &&
                                                            `: ${item.result.error}`}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {/* Default fallback for any unhandled types */}
                        {!HANDLED_TYPES.includes(getItemType(item)) && (
                            <div className="flex items-start gap-2">
                                <Bot className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-gray-400 font-medium">
                                            {item.result
                                                ? "Completed:"
                                                : "Working..."}
                                        </span>
                                        <span className="text-gray-300 break-words text-[10px]">
                                            {item.toolName ||
                                                getItemType(item).replace(
                                                    /_/g,
                                                    " "
                                                )}
                                        </span>
                                        <SubagentBadge
                                            agentName={item.agentName}
                                        />
                                    </div>
                                    {/* Show file path if available */}
                                    {(item.args?.file_path ||
                                        item.args?.filePath ||
                                        item.result?.file_path) && (
                                        <div className="mt-1 text-gray-400 text-[10px]">
                                            {item.args?.file_path ||
                                                item.args?.filePath ||
                                                item.result?.file_path}
                                        </div>
                                    )}
                                    {/* Show reason/description if available */}
                                    {(getReasonText(item.args?.reason) ||
                                        item.args?.description ||
                                        item.description) && (
                                        <div className="mt-1 text-gray-300 text-[10px]">
                                            <ExpandableText
                                                text={
                                                    getReasonText(
                                                        item.args?.reason
                                                    ) ||
                                                    item.args?.description ||
                                                    item.description
                                                }
                                                maxLength={150}
                                            />
                                        </div>
                                    )}
                                    {item.result && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {item.result.success ? (
                                                <CheckCircle className="h-3 w-3 text-green-400" />
                                            ) : (
                                                <XCircle className="h-3 w-3 text-red-400" />
                                            )}
                                            <span
                                                className={`${
                                                    item.result.success
                                                        ? "text-green-400"
                                                        : "text-red-400"
                                                } text-[10px]`}
                                            >
                                                {item.result.message ||
                                                    (item.result.success
                                                        ? "Success"
                                                        : "Failed")}
                                                {item.result.error &&
                                                    `: ${item.result.error}`}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
};
